import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, Radio, Users } from 'lucide-react';
import { useSecurityContext, type Sede, type ContactoCRA } from '@/contexts/MaintenanceContext';

interface SedeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clienteId: string;
  sede?: Sede | null;
  onSaved?: (sede: Sede) => void;
}

const empty = {
  nombre: '',
  direccion: '',
  codigo_postal: '',
  poblacion: '',
  provincia: '',
  contacto_nombre: '',
  contacto_telefono: '',
  contacto_email: '',
  notas: '',
  numero_abonado: '',
  numero_abonado_incendio: '',
  contrasena_cra: '',
  palabra_clave_cra: '',
  palabra_coaccion: '',
  horario_apertura: '',
  horario_cierre: '',
  contactos_cra: [] as ContactoCRA[],
};

export const SedeFormDialog: React.FC<SedeFormDialogProps> = ({ open, onOpenChange, clienteId, sede, onSaved }) => {
  const { crearSede, editarSede } = useSecurityContext();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        nombre: sede?.nombre || '',
        direccion: sede?.direccion || '',
        codigo_postal: sede?.codigo_postal || '',
        poblacion: sede?.poblacion || '',
        provincia: sede?.provincia || '',
        contacto_nombre: sede?.contacto_nombre || '',
        contacto_telefono: sede?.contacto_telefono || '',
        contacto_email: sede?.contacto_email || '',
        notas: sede?.notas || '',
        numero_abonado: sede?.numero_abonado || '',
        numero_abonado_incendio: (sede as any)?.numero_abonado_incendio || '',
        contrasena_cra: sede?.contrasena_cra || '',
        palabra_clave_cra: sede?.palabra_clave_cra || '',
        palabra_coaccion: sede?.palabra_coaccion || '',
        horario_apertura: sede?.horario_apertura || '',
        horario_cierre: sede?.horario_cierre || '',
        contactos_cra: Array.isArray(sede?.contactos_cra) ? sede!.contactos_cra! : [],
      });
    }
  }, [open, sede]);

  const updateContacto = (idx: number, field: keyof ContactoCRA, value: string) => {
    const updated = [...form.contactos_cra];
    while (updated.length <= idx) updated.push({ nombre: '', telefono: '', cargo: '' });
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, contactos_cra: updated });
  };

  const addContacto = () =>
    setForm({ ...form, contactos_cra: [...form.contactos_cra, { nombre: '', telefono: '', cargo: '' }] });

  const removeContacto = (idx: number) =>
    setForm({ ...form, contactos_cra: form.contactos_cra.filter((_, i) => i !== idx) });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      // Limpiar contactos vacíos
      const payload = {
        ...form,
        contactos_cra: form.contactos_cra.filter(c => c.nombre?.trim()),
      };
      if (sede) {
        await editarSede(sede.id, payload);
        onSaved?.({ ...sede, ...payload } as Sede);
      } else {
        const nueva = await crearSede({ cliente_id: clienteId, ...payload });
        if (nueva) onSaved?.(nueva);
      }
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sede ? 'Editar sede' : 'Nueva sede'}</DialogTitle>
          <DialogDescription>
            Una sede es un emplazamiento físico del cliente. Cada sede puede tener su propio abonado CRA y sus contactos autorizados.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Identificación y dirección */}
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre de la sede *</Label>
              <Input id="nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required placeholder="Ej. Sede Central, Almacén Norte..." />
            </div>
            <div className="space-y-2">
              <Label htmlFor="direccion">Dirección</Label>
              <Input id="direccion" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="Calle y número" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cp">C.P.</Label>
                <Input id="cp" value={form.codigo_postal} onChange={(e) => setForm({ ...form, codigo_postal: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="poblacion">Población</Label>
                <Input id="poblacion" value={form.poblacion} onChange={(e) => setForm({ ...form, poblacion: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="provincia">Provincia</Label>
                <Input id="provincia" value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} />
              </div>
            </div>
          </div>

          <Separator />

          {/* Contacto principal del emplazamiento */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Contacto del emplazamiento</h4>
            <p className="text-xs text-muted-foreground -mt-1">Responsable presencial (llaves, acceso, obra).</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cn">Nombre</Label>
                <Input id="cn" value={form.contacto_nombre} onChange={(e) => setForm({ ...form, contacto_nombre: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ct">Teléfono</Label>
                <Input id="ct" value={form.contacto_telefono} onChange={(e) => setForm({ ...form, contacto_telefono: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ce">Email</Label>
                <Input id="ce" type="email" value={form.contacto_email} onChange={(e) => setForm({ ...form, contacto_email: e.target.value })} />
              </div>
            </div>
          </div>

          <Separator />

          {/* CRA */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Radio className="h-4 w-4 text-primary" />
              Configuración CRA de esta sede
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="abonado">Nº abonado intrusión</Label>
                <Input id="abonado" value={form.numero_abonado} onChange={(e) => setForm({ ...form, numero_abonado: e.target.value })} placeholder="AB-99001" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="abonado_incendio">Nº abonado incendios</Label>
                <Input id="abonado_incendio" value={form.numero_abonado_incendio} onChange={(e) => setForm({ ...form, numero_abonado_incendio: e.target.value })} placeholder="AB-99002" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contrasena_cra">Contraseña CRA</Label>
                <Input id="contrasena_cra" value={form.contrasena_cra} onChange={(e) => setForm({ ...form, contrasena_cra: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="palabra_clave_cra">Palabra clave</Label>
                <Input id="palabra_clave_cra" value={form.palabra_clave_cra} onChange={(e) => setForm({ ...form, palabra_clave_cra: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="palabra_coaccion">Palabra de coacción</Label>
                <Input id="palabra_coaccion" value={form.palabra_coaccion} onChange={(e) => setForm({ ...form, palabra_coaccion: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="horario_apertura">Horario apertura</Label>
                <Input id="horario_apertura" type="time" value={form.horario_apertura} onChange={(e) => setForm({ ...form, horario_apertura: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="horario_cierre">Horario cierre</Label>
                <Input id="horario_cierre" type="time" value={form.horario_cierre} onChange={(e) => setForm({ ...form, horario_cierre: e.target.value })} />
              </div>
            </div>
          </div>

          <Separator />

          {/* Contactos CRA */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Contactos CRA autorizados
            </h4>
            <p className="text-xs text-muted-foreground -mt-1">Personas a las que la CRA puede llamar ante una alarma de esta sede.</p>
            {form.contactos_cra.length === 0 && (
              <p className="text-sm text-muted-foreground border border-dashed rounded-lg py-4 text-center">Sin contactos. Añade al menos uno si esta sede tiene servicio CRA.</p>
            )}
            {form.contactos_cra.map((c, idx) => (
              <div key={idx} className="border rounded-lg p-3 space-y-2 bg-card">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Contacto {idx + 1}</p>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeContacto(idx)} className="h-8 w-8 p-0">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nombre</Label>
                    <Input value={c.nombre || ''} onChange={(e) => updateContacto(idx, 'nombre', e.target.value)} placeholder="Juan López" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Teléfono</Label>
                    <Input value={c.telefono || ''} onChange={(e) => updateContacto(idx, 'telefono', e.target.value)} placeholder="612 345 678" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Cargo</Label>
                    <Input value={c.cargo || ''} onChange={(e) => updateContacto(idx, 'cargo', e.target.value)} placeholder="Gerente" />
                  </div>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addContacto} className="w-full">
              <Plus className="h-4 w-4 mr-1" /> Añadir contacto CRA
            </Button>
          </div>

          <Separator />

          {/* Notas */}
          <div className="space-y-2">
            <Label htmlFor="notas">Notas</Label>
            <Textarea id="notas" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving || !form.nombre.trim()}>{sede ? 'Guardar' : 'Crear sede'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
